// Advanced Del/PU filtering logic for dropdown
import { Component, AfterViewInit, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { DialogService } from '../../../services/dialog.service';
import { CountryPhoneCodes } from '@app/components/interface/country-phone-codes';
import { OrderService } from '@app/services/order.service';
import { Observable, Subscription, take, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { CompaniesService } from '@app/services/companies.service';
import { AddressService } from '@app/services/address.service';
import { ToastrService } from 'ngx-toastr';
import { Hr12to24hrPipe } from '@app/hr12to24hr.pipe';
import * as moment from 'moment';
import { AuthServiceService } from '@app/services/auth-service.service';
import { MatDialog } from '@angular/material/dialog';
import { ErrormodalComponent } from '../errormodal/errormodal.component';
import { ToastService } from '@app/services/toast.service';
import { AddAddressListingComponent } from '../add-address-listing/add-address-listing.component';
import { ConfirmExitDialogComponent } from '../confirm-exit-dialog/confirm-exit-dialog.component';
import { NavigationStart, Router } from '@angular/router';
import { ShareddtaService } from '@app/services/shareddta.service';
import { BreadcrumbService } from '@app/services/breadcrumb.service';
export interface BulkOrderRow {
  rowNumber: number;
  id: string;
  orderNo: string;
  warehouse: string;
  companyName: string;
  companyAddress: string;
  contactName: string;
  countryCode: string;
  phone: string;
  altPhone: string;
  email: string;
  deliveryDate: string;
  pickupDate: string;
  deliveryTime: string;
  pickupTime: string;
  serviceTime: string;
  driver_id: any;
  signReq: string;
  delPU: any;
  note: string;
  showCompanyDropdown: boolean;
  showAddressDropdown: boolean;
  showAddButton?: boolean;
  filteredCompanies: any;
  filteredAddressOptions: any;
  highlightedCompanyIndex: any;
  highlightedAddressIndex: any;
  addressId?: any;
  isAddressNew?: boolean;
  isCompanyNew?: boolean;
  matchingCompanies?: any[];
  matchingAddresses?: any[];
  originalCompanyName?: string;
  originalCompanyAddress?: string;
  isBackendOrder?: boolean;
  fromDispatchSheet?: boolean;
  row_uuid?: string;
  sheetContactName?: string;
  sheetPhone?: string;
  orders_logs?: any;
}
import { ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';

@Component({
  selector: 'app-multiple-add-order',
  templateUrl: './multiple-add-order.component.html',
  styleUrl: './multiple-add-order.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class MultipleAddOrderComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;
  hasFromDispatchSheet: boolean = false;
  rows: BulkOrderRow[] = [];
  rowCount: number = 0;
  minDate: string = '';
  today: string;
  isLoading: boolean = true;
  private isDriversLoaded: boolean = false;
  private isDraftLoaded: boolean = false;
  isSavingDraft: boolean = false;
  isAccessingSheet: boolean = false;

  isAddingOrder: boolean = false;
  defaultCountryCode: string = '';
  private countryCodeSubscription!: Subscription;
  // Dropdown options
  warehouseOptions = [
    { value: 'Delivery', label: 'Delivery' },
    { value: 'Pickup', label: 'Pickup' },
  ];

  countryCodeOptions = CountryPhoneCodes;

  serviceTimeOptions = [
    { value: 't0', label: 'T0' },
    { value: 't15', label: 'T15' },
    { value: 't30', label: 'T30' },
    { value: 't45', label: 'T45' },
    { value: 't60', label: 'T60' }
  ];

  driverOptions: any = [];
  yesNoOptions = [
    { value: 'No', label: 'No' },
    { value: 'Yes', label: 'Yes' }
  ];
  Delpu = Array.from({ length: 20 }, (_, i) => ({
    value: i + 1,
    label: i + 1
  }));
  initialCompanies: any[] = [];
  initialAddresses: any[] = [];

  constructor(private dialogService: DialogService, private orderService: OrderService, private companyService: CompaniesService, private addressService: AddressService, private toastr: ToastrService, private timeConverter: Hr12to24hrPipe, public authService: AuthServiceService, private dialog: MatDialog, private toast: ToastService, private router: Router, private shareddta: ShareddtaService, private cdr: ChangeDetectorRef, private breadcrumbService: BreadcrumbService) {
    // Set today's date in YYYY-MM-DD format
    // const now = new Date();
    // this.today = now.toISOString().split('T')[0];
    localStorage.setItem('isDataModified', 'false');
    const nextWorkingDate = this.calculateNextWorkingDate(new Date());
    this.today = this.dateFormat(nextWorkingDate);
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.isDataModified && this.hasImportantData()) {
      return new Observable<boolean>((observer) => {
        const dialogRef = this.dialog.open(ConfirmExitDialogComponent, {
          width: '450px',
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result === true) {
            // Save & Exit case
            this.saveDraft();
            observer.next(true);
            observer.complete();
          } else if (result === false) {
            // Exit without saving (as per user: "if user press cancel from pop up modal then we will close the bulk order")
            localStorage.setItem('isDataModified', 'false');
            observer.next(true);
            observer.complete();
          } else {
            // Stay on page (modal dismissed/clicked outside if allowed)
            observer.next(false);
            observer.complete();
          }
        });
      });
    }
    return true;
  }

  ngOnInit(): void {
    this.breadcrumbService.set([
      { label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', action: () => this.router.navigate(['/admin/dashboard']) },
      { label: 'Bulk Add Orders' }
    ]);
    // Setup country code subscription
    this.countryCodeSubscription = this.shareddta.countryCode$.subscribe(code => {
      if (code) {
        this.defaultCountryCode = code;
        // Update any row that doesn't have a phone number yet or hasn't had country code changed
        this.rows.forEach(row => {
          if (!row.phone && !row.altPhone) {
            row.countryCode = code;
          }
        });
        this.cdr.markForCheck();
      }
    });

    // Start fetching data in parallel
    this.getActiveDrivers();
    this.loadDraftData();

    // Fetch initial page 1 companies and addresses for initial suggestions
    this.companyService.companySearchObservable(1).subscribe((data: any) => {
      const results = data?.result?.results || (Array.isArray(data) ? data : []);
      this.initialCompanies = results;
      this.cdr.markForCheck();
    });

    this.addressService.addressSearchObservable(1).subscribe((data: any) => {
      const results = data?.result?.results || (Array.isArray(data) ? data : []);
      this.initialAddresses = results;
      this.cdr.markForCheck();
    });

  }



  ngAfterViewInit(): void {
    this.shareddta.signalPageReady();
  }

  /**
   * Add new rows to the table
   * @param count Number of rows to add (default: 5)
   */
  addRows(count: number = 10): void {
    const newRows: BulkOrderRow[] = [];
    for (let i = 0; i < count; i++) {
      const rowCount = this.rows.length + newRows.length + 1;
      newRows.push(this.createEmptyRow(rowCount));
    }
    this.rows = [...this.rows, ...newRows];
    this.cdr.markForCheck();
    this.toast.show('success', `${count} rows added`);

    // Scroll to bottom so user sees the new rows
    setTimeout(() => {
      const container = document.getElementById('tableScrollContainer');
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    }, 50); // small delay to let DOM update first
  }

  /**
   * Create an empty row with default values
   */
  private createEmptyRow(rowNumber: number): BulkOrderRow {
    const savedCountryCode = localStorage.getItem('countryCode');
    return {
      rowNumber,
      id: '',
      orderNo: '',
      warehouse: '',
      companyName: '',
      companyAddress: '',
      contactName: '',
      countryCode: this.defaultCountryCode || savedCountryCode || '',
      phone: '',
      altPhone: '',
      email: '',
      deliveryDate: this.today,
      pickupDate: this.today,
      deliveryTime: '',
      pickupTime: '',
      serviceTime: 't15',
      driver_id: null,
      signReq: 'No',
      delPU: 0,
      note: '',
      showCompanyDropdown: false,
      showAddressDropdown: false,
      filteredCompanies: [],
      filteredAddressOptions: [],
      highlightedCompanyIndex: -1,
      highlightedAddressIndex: -1,
      fromDispatchSheet: false,
    };
  }

  get hasActiveRows(): boolean {
    return this.rows && this.rows.some(row =>
      row.id ||
      row.orderNo ||
      row.warehouse ||
      row.companyName ||
      row.companyAddress ||
      row.contactName ||
      row.phone ||
      row.altPhone ||
      row.email ||
      row.driver_id ||
      row.note
    );
  }

  /**
   * Delete a specific row
   */
  deleteDraftid: any;
  async deleteRow(rowNumber: number, row: any): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Delete Row',
      'Are you sure you want to delete this row? This action cannot be undone.',
      'Delete',
      'Cancel'
    );

    if (confirmed.confirmed) {
      console.log(this.rows);
      const currentRow = this.rows.find(row => row.rowNumber === rowNumber);
      if (row.id) {
        this.deleteDraftid = row.id;
        this.draftDelete()
      }
      else if (currentRow?.orderNo || currentRow?.companyAddress || currentRow?.companyName || currentRow?.warehouse || currentRow?.driver_id || currentRow?.note) {
        this.rows = this.rows.map(row => {
          if (row.rowNumber === rowNumber) {
            return this.createEmptyRow(rowNumber); // same row replace
          }
          return row;
        });
      }
      this.cdr.markForCheck();
      // this.rows = this.rows.filter(row => row.rowNumber !== rowNumber);
    }
  }
  draftDelete() {
    var formdata = new FormData();
    formdata.append('order_ids', JSON.stringify([this.deleteDraftid]));
    console.log("FormData Value:", formdata.get('order_ids'));
    this.orderService.orderDelete(formdata).subscribe(
      (data: any) => {
        if (this.authService.resultCodeError(data)) {
          return;
        }
        this.loadDraftData();
        if (data.resultDescription) {
          this.toast.show('success', data?.resultDescription);
        }
      },
      (error) => {
        this.authService.dataError(error);
      }
    );
  }

  async clearAllRows(): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Clear All Rows',
      'Are you sure you want to clear all rows? This will delete all draft orders and cannot be undone.',
      'Clear All',
      'Cancel'
    );

    if (confirmed.confirmed) {
      const draftIds = this.rows.filter(row => row.id).map(row => row.id);

      if (draftIds.length > 0) {
        this.isLoading = true;
        this.cdr.markForCheck();
        const formdata = new FormData();
        formdata.append('order_ids', JSON.stringify(draftIds));
        this.orderService.orderDelete(formdata).subscribe({
          next: (data: any) => {
            this.isLoading = false;
            if (this.authService.resultCodeError(data)) {
              this.cdr.markForCheck();
              return;
            }
            this.isDataModified = false;
            localStorage.setItem('isDataModified', 'false');
            this.toast.show('success', 'All draft rows cleared successfully');
            this.resetToEmptyRows();
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.isLoading = false;
            this.authService.dataError(error);
            this.cdr.markForCheck();
          }
        });
      } else {
        this.resetToEmptyRows();
        this.isDataModified = false;
        localStorage.setItem('isDataModified', 'false');
        this.toast.show('success', 'All rows cleared');
        this.cdr.markForCheck();
      }
    }
  }

  resetToEmptyRows(): void {
    this.rows = [];
    const savedCountryCode = localStorage.getItem('countryCode');
    for (let i = 0; i < 10; i++) {
      this.rows.push({
        id: '',
        rowNumber: i + 1,
        orderNo: '',
        warehouse: '',
        companyName: '',
        companyAddress: '',
        contactName: '',
        countryCode: this.defaultCountryCode || savedCountryCode || '',
        phone: '',
        altPhone: '',
        email: '',
        deliveryDate: this.today,
        pickupDate: this.today,
        deliveryTime: '',
        pickupTime: '',
        serviceTime: 't15',
        driver_id: null,
        signReq: 'No',
        delPU: 0,
        note: '',
        showCompanyDropdown: false,
        showAddressDropdown: false,
        filteredCompanies: [],
        filteredAddressOptions: [],
        highlightedCompanyIndex: -1,
        highlightedAddressIndex: -1,
        fromDispatchSheet: false,
      });
    }
  }



  /**
   * Save draft
   */
  //   saveDraft(): void {
  //     const filledRows = this.rows.filter(row =>
  //       row.orderNo || row.companyName || row.companyAddress
  //     );

  // const draftPayload = {
  //   orders: filledRows.map(row => ({
  //     is_draft: 'true',
  //     order_id: row.id || null,
  //     rownumber: row.rowNumber,

  //     order_no: row.orderNo || null,
  //     warehouse: row.warehouse || null,
  //     company_name: row.companyName || null,
  //     address: row.companyAddress || null,
  //     contact_name: row.contactName || null,

  //     country_code: row.countryCode || null,
  //     phone: row.phone || null,
  //     alt_phone: row.altPhone === 'null' ? null : row.altPhone,
  //     email: row.email || null,

  //     dely_date: row.deliveryDate || null,
  //     pickup_date: row.pickupDate || null,
  //     dely_time: row.deliveryTime || null,
  //     pickup_time: row.pickupTime || null,
  //     service_time: row.serviceTime || null,

  //     driver_id: row.driver_id || null,

  //     signature_required: row.signReq || 'No',   // ✅ Yes / No
  //     first_delivery: row.delPU || 'No',         // ✅ Yes / No

  //     note: row.note || null,
  //   })),
  //   skip_draft_orders_saved: false
  // };

  // console.log('Draft Payload:', draftPayload);
  //     alert(`Draft saved with ${filledRows.length} orders!`);

  //     // Here you would typically call a service to save the draft
  //     // this.orderService.saveDraft(filledRows).subscribe(...);
  //   }
  dateMomentObjFormat(d: any) {
    if (typeof d === 'object') {
      let date = d._d;
      // Adjust the date to UTC before formatting
      date = moment(date).utc().toDate(); // Convert to UTC to avoid timezone issues
      return date.getFullYear() + '-' + this.padNumber(date.getMonth() + 1) + '-' + this.padNumber(date.getDate());
    } else {
      return d;
    }
  }
  getTime(milliseconds?: any) {
    if (milliseconds) {
      const currentDate = new Date();
      const hours = currentDate.getHours().toString().padStart(2, '0');
      const minutes = currentDate.getMinutes().toString().padStart(2, '0');
      const seconds = currentDate.getSeconds().toString().padStart(2, '0');
      const milliseconds = currentDate.getMilliseconds().toString().padStart(3, '0');
      return `${hours}:${minutes}:${seconds}.${milliseconds}`;
    } else {
      return new Date().toLocaleTimeString('en-US', { hour: 'numeric', hour12: true, minute: 'numeric' });
    }
  }
  getFormattedDate(dateString: any) {
    const date = moment(dateString);
    if (!date.isValid()) {
      // Fallback to current time if parsing fails to prevent complete crash, 
      // but ideally we fixed the source of invalidity
      return moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + "+00";
    }

    const utcDate = date.utc();
    const formattedDate = utcDate.format('YYYY-MM-DDTHH:mm:ss.SSS') + "+00";
    return formattedDate;
  }
  isDataModified: boolean = false; // Track if data is modified
  saveDraft(targetUrl?: any): void {

    const filledRows = this.rows.filter(row =>
      row.orderNo || row.companyName || row.companyAddress
    );

    if (filledRows.length === 0) {
      this.toast.show('error', 'No valid rows to save draft');
      localStorage.setItem('isDataModified', 'false');
      this.isDataModified = false;
      return;
    }

    if (!this.validateFilledRows(filledRows)) {
      return;
    }

    // 🔹 Backup original values (like addDraft)
    const savedPickupTimes = filledRows.map(row => row.pickupTime);
    const savedDelyTimes = filledRows.map(row => row.deliveryTime);
    const savedPickupDates = filledRows.map(row => row.pickupDate);
    const savedDelyDates = filledRows.map(row => row.deliveryDate);

    // 🔹 SAME DATE formatting logic
    filledRows.forEach(row => {
      const c = row.pickupDate
        ? this.dateMomentObjFormat(row.pickupDate) + ' ' + this.getTime(true)
        : null;
      row.pickupDate = c ? this.getFormattedDate(c) : 'null';

      const d = row.deliveryDate
        ? this.dateMomentObjFormat(row.deliveryDate) + ' ' + this.getTime(true)
        : null;
      row.deliveryDate = d ? this.getFormattedDate(d) : 'null';
    });

    // 🔹 SAME TIME formatting logic
    filledRows.forEach(row => {
      const timeFormatRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i;

      row.pickupTime = row.pickupTime
        ? (timeFormatRegex.test(row.pickupTime)
          ? this.timeConverter.transform(row.pickupTime, '12-24')
          : row.pickupTime)
        : null;

      row.deliveryTime = row.deliveryTime
        ? (timeFormatRegex.test(row.deliveryTime)
          ? this.timeConverter.transform(row.deliveryTime, '12-24')
          : row.deliveryTime)
        : null;
    });
    const skipKeysRaw = localStorage.getItem('skip_draft_orders_saved');
    const skip_draft_orders_saved: string[] = skipKeysRaw ? JSON.parse(skipKeysRaw) : [];

    // 🔹 Payload build (unchanged mapping)
    const draftPayload = {
      orders: filledRows.map(row => ({
        is_draft: 'true',
        order_id: row.id || null,
        rownumber: row.rowNumber,

        order_no: row.orderNo || null,
        warehouse: row.warehouse || null,
        company_name: row.companyName || null,
        address: row.companyAddress || null,
        contact_name: row.contactName || null,

        country_code: row.countryCode || null,
        phone: row.phone || null,
        alt_phone: row.altPhone === 'null' ? null : row.altPhone,
        email: row.email || null,

        dely_date: row.deliveryDate,
        pickup_date: row.pickupDate,
        dely_time: row.deliveryTime,
        pickup_time: row.pickupTime,
        service_time: row.serviceTime || null,

        driver_id: row.driver_id || null,
        signature_required: row.signReq || 'No',
        first_delivery: (row.delPU !== null && row.delPU !== undefined) ? row.delPU : 'No',
        note: row.note || null,
        ...(row.orders_logs ? { orders_logs: row.orders_logs } : {}),
      })),
      skip_draft_orders_saved: skip_draft_orders_saved,
      from_dispatch_sheet: filledRows.some(row => row.fromDispatchSheet) || this.rows.some(row => row.fromDispatchSheet) || this.hasFromDispatchSheet
    };

    // // 🔥 Print EXACT API payload
    // console.log('🚀 Draft API Payload:', JSON.stringify(draftPayload, null, 2));

    // alert(`Draft saved with ${filledRows.length} orders!`);

    // 🔁 Restore original values (important, like addDraft)
    filledRows.forEach((row, index) => {
      row.pickupTime = savedPickupTimes[index];
      row.deliveryTime = savedDelyTimes[index];
      row.pickupDate = savedPickupDates[index];
      row.deliveryDate = savedDelyDates[index];
    });
    this.orderService.addMultipledraftOrders(draftPayload).subscribe({
      next: (response: any) => {
        if (response.resultCode === '1') {
          this.toast.show('success', response.resultDescription);
          this.loadDraftData();
          localStorage.setItem('isDataModified', 'false');
          this.isDataModified = false;
          this.cdr.markForCheck();
          setTimeout(() => {
            if (!this.isDataModified) {
              localStorage.setItem('isDataModified', 'false');
              this.router.navigateByUrl(targetUrl);
            }

          }, 200);
        }
        if (response.resultCode === '0') {
          if (response.invalidRows && response.invalidRows.length > 0) {
            this.openErrorModal(response.invalidRows, response.add_star, response.is_merge);
          } else {
            if (response.errorMessage) {
              this.toast.show('error', response.errorMessage);
            }
          }
          filledRows.forEach((order, index) => {
            order.pickupTime = savedPickupTimes[index];
            order.deliveryTime = savedDelyTimes[index];
            order.deliveryDate = savedPickupDates[index];
            order.pickupDate = savedDelyDates[index];
          });
        }
        if (response.resultCode === '4') {
          this.authService.doLogout();
          this.toast.show('success', 'Logout!');
        }
      },
      error: (error) => {
        console.error('Save Draft API Error:', error);
        this.toast.show('error', 'Failed to add orders. Please try again.');
        this.isSavingDraft = false;
      },
    });


    // 🔹 Actual API call (when ready)
    // this.orderService.addMultipledraftOrders(draftPayload).subscribe(...)
  }
  saveDraftBtn(isAfterMerge: boolean = false): void {
    const filledRows = this.rows.filter(row =>
      row.orderNo || row.companyName || row.companyAddress
    );

    if (filledRows.length === 0 && !isAfterMerge) {
      this.toast.show('error', 'No valid rows to save draft');
      localStorage.setItem('isDataModified', 'false');
      this.isDataModified = false;
      return;
    }

    if (filledRows.length > 0 && !this.validateFilledRows(filledRows)) {
      return;
    }

    this.isSavingDraft = true;

    // 🔹 Backup original values (like addDraft)
    const savedPickupTimes = filledRows.map(row => row.pickupTime);
    const savedDelyTimes = filledRows.map(row => row.deliveryTime);
    const savedPickupDates = filledRows.map(row => row.pickupDate);
    const savedDelyDates = filledRows.map(row => row.deliveryDate);

    // 🔹 SAME DATE formatting logic
    filledRows.forEach(row => {
      const c = row.pickupDate
        ? this.dateMomentObjFormat(row.pickupDate) + ' ' + this.getTime(true)
        : null;
      row.pickupDate = c ? this.getFormattedDate(c) : 'null';

      const d = row.deliveryDate
        ? this.dateMomentObjFormat(row.deliveryDate) + ' ' + this.getTime(true)
        : null;
      row.deliveryDate = d ? this.getFormattedDate(d) : 'null';
    });

    // 🔹 SAME TIME formatting logic
    filledRows.forEach(row => {
      const timeFormatRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i;

      row.pickupTime = row.pickupTime
        ? (timeFormatRegex.test(row.pickupTime)
          ? this.timeConverter.transform(row.pickupTime, '12-24')
          : row.pickupTime)
        : null;

      row.deliveryTime = row.deliveryTime
        ? (timeFormatRegex.test(row.deliveryTime)
          ? this.timeConverter.transform(row.deliveryTime, '12-24')
          : row.deliveryTime)
        : null;
    });
    const skipKeysRaw = localStorage.getItem('skip_draft_orders_saved');
    const skip_draft_orders_saved: string[] = skipKeysRaw ? JSON.parse(skipKeysRaw) : [];

    // 🔹 Payload build (unchanged mapping)
    const draftPayload = {
      orders: filledRows.map(row => ({
        is_draft: 'true',
        order_id: row.id || null,
        rownumber: row.rowNumber,

        order_no: row.orderNo || null,
        warehouse: row.warehouse || null,
        company_name: row.companyName || null,
        address: row.companyAddress || null,
        contact_name: row.contactName || null,

        country_code: row.countryCode || null,
        phone: row.phone || null,
        alt_phone: row.altPhone === 'null' ? null : row.altPhone,
        email: row.email || null,

        dely_date: row.deliveryDate,
        pickup_date: row.pickupDate,
        dely_time: row.deliveryTime,
        pickup_time: row.pickupTime,
        service_time: row.serviceTime || null,

        driver_id: row.driver_id || null,
        signature_required: row.signReq || 'No',
        first_delivery: (row.delPU !== null && row.delPU !== undefined) ? row.delPU : 'No',
        note: row.note || null,
        ...(row.orders_logs ? { orders_logs: row.orders_logs } : {}),
      })),
      skip_draft_orders_saved: skip_draft_orders_saved,
      from_dispatch_sheet: filledRows.some(row => row.fromDispatchSheet) || this.rows.some(row => row.fromDispatchSheet) || this.hasFromDispatchSheet
    };

    // // 🔥 Print EXACT API payload
    // console.log('🚀 Draft API Payload:', JSON.stringify(draftPayload, null, 2));

    // alert(`Draft saved with ${filledRows.length} orders!`);

    // 🔁 Restore original values (important, like addDraft)
    filledRows.forEach((row, index) => {
      row.pickupTime = savedPickupTimes[index];
      row.deliveryTime = savedDelyTimes[index];
      row.pickupDate = savedPickupDates[index];
      row.deliveryDate = savedDelyDates[index];
    });
    this.orderService.addMultipledraftOrders(draftPayload).subscribe({
      next: (response: any) => {
        this.isSavingDraft = false;
        if (response.resultCode === '1') {
          this.toast.show('success', response.resultDescription);
          this.loadDraftData();
          localStorage.setItem('isDataModified', 'false');
          this.isDataModified = false;
          this.cdr.markForCheck();
        }
        if (response.resultCode === '0') {
          if (response.invalidRows && response.invalidRows.length > 0) {
            this.openErrorModal(response.invalidRows, response.add_star, response.is_merge);
          } else {
            if (response.errorMessage) {
              this.toast.show('error', response.errorMessage);
            }
          }
          filledRows.forEach((order, index) => {
            order.pickupTime = savedPickupTimes[index];
            order.deliveryTime = savedDelyTimes[index];
            order.deliveryDate = savedPickupDates[index];
            order.pickupDate = savedDelyDates[index];
          });
        }
        if (response.resultCode === '4') {
          this.authService.doLogout();
          this.toast.show('success', 'Logout!');
        }
      },
      error: (error) => {
        console.error('Save Draft Button API Error:', error);
        this.toast.show('error', 'Failed to add orders. Please try again.');
        this.isSavingDraft = false;
      },
    });
  }

  openPortalSheet(): void {
    this.isAccessingSheet = true;
    this.cdr.markForCheck();
    this.orderService.getDispatchSheetUrl().subscribe({
      next: () => {
        this.isAccessingSheet = false;
        this.cdr.markForCheck();
        this.router.navigate(['/admin/dispatch-sheet']);
      },
      error: (error) => {
        console.error('Error fetching dispatch sheet URL:', error);
        this.isAccessingSheet = false;
        this.cdr.markForCheck();
        // Redirect anyway as requested
        this.router.navigate(['/admin/dispatch-sheet']);
      }
    });
  }

  async importSheet(): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Import Sheet',
      'Are you sure you want to import orders from the dispatch sheet?',
      'Import',
      'Cancel',
      'info'
    );

    if (!confirmed?.confirmed) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.orderService.syncDispatchSheet().subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response && response.resultCode === '1') {
          const isZeroOrders = response.resultDescription && /\b0\s+orders?\b/i.test(response.resultDescription);
          if (isZeroOrders) {
            this.rows = [];
            this.toast.show('warning', response.resultDescription);
          } else if (Array.isArray(response.result) && response.result.length > 0) {
            this.toast.show('success', response.resultDescription || 'Orders imported successfully');
            const draftRows = this.rows
              .filter(row => row.isBackendOrder || (row.id && !row.fromDispatchSheet && !row.row_uuid))
              .map((row, idx) => ({ ...row, rowNumber: idx + 1 }));

            const sheetRows = response.result.map((item: any, index: number) => {
              return {
                id: item.order_id || '',
                rowNumber: draftRows.length + index + 1,
                orderNo: item.order_no || '',
                warehouse: item.warehouse || 'Delivery',
                companyName: item.company_name || '',
                companyAddress: item.address || '',
                contactName: item.contact_name || '',
                countryCode: item.country_code || this.defaultCountryCode || '',
                phone: item.phone || '',
                altPhone: item.alt_phone || '',
                email: item.email || '',
                deliveryDate: item.dely_date ? item.dely_date.split('T')[0] : this.today,
                pickupDate: item.pickup_date ? item.pickup_date.split('T')[0] : this.today,
                deliveryTime: item.dely_time || '',
                pickupTime: item.pickup_time || '',
                serviceTime: item.service_time || 't15',
                driver_id: item.driver_id || null,
                signReq: item.signature_required || 'No',
                delPU: item.first_delivery !== null && item.first_delivery !== undefined ? item.first_delivery : 0,
                note: item.note || '',
                showCompanyDropdown: false,
                showAddressDropdown: false,
                filteredCompanies: [],
                filteredAddressOptions: [],
                highlightedCompanyIndex: -1,
                highlightedAddressIndex: -1,
                isAddressNew: item.is_address_new || false,
                isCompanyNew: item.is_company_new || false,
                matchingCompanies: item.matching_companies || [],
                matchingAddresses: item.matching_addresses || [],
                addressId: (!item.is_address_new && item.matching_addresses?.length === 1) ? item.matching_addresses[0].id : null,
                fromDispatchSheet: true,
                row_uuid: item.row_uuid || '',
                sheetContactName: item.contact_name || '',
                sheetPhone: item.phone || '',
                orders_logs: item.orders_logs || null
              };
            });

            const mergedRows = [...draftRows, ...sheetRows];
            const totalNeeded = Math.max(10, mergedRows.length);
            while (mergedRows.length < totalNeeded) {
              mergedRows.push(this.createEmptyRow(mergedRows.length + 1));
            }
            this.rows = mergedRows;

            this.hasFromDispatchSheet = true;
            this.isDataModified = true;
            localStorage.setItem('isDataModified', 'true');
          } else {
            const draftRows = this.rows
              .filter(row => row.isBackendOrder || (row.id && !row.fromDispatchSheet && !row.row_uuid))
              .map((row, idx) => ({ ...row, rowNumber: idx + 1 }));
            while (draftRows.length < 10) {
              draftRows.push(this.createEmptyRow(draftRows.length + 1));
            }
            this.rows = draftRows;
            this.toast.show('warning', response.resultDescription || 'Sheet does not contain any orders');
          }
        } else {
          this.toast.show('error', response?.resultDescription || 'Failed to sync sheet');
        }
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.isLoading = false;
        this.toast.show('error', 'An error occurred while syncing the sheet. Please try again.');
        this.cdr.markForCheck();
      }
    });
  }

  onFileSelected(event: any): void {
    const file = event.target?.files?.[0];
    if (!file) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.orderService.uploadOrderExcelSheet(file).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response && response.resultCode === '1') {
          if (Array.isArray(response.result) && response.result.length > 0) {
            this.toast.show('success', response.resultDescription || 'Orders imported successfully');
            const draftRows = this.rows
              .filter(row => row.isBackendOrder || (row.id && !row.fromDispatchSheet && !row.row_uuid))
              .map((row, idx) => ({ ...row, rowNumber: idx + 1 }));

            const sheetRows = response.result.map((item: any, index: number) => {
              return {
                id: item.order_id || '',
                rowNumber: draftRows.length + index + 1,
                orderNo: item.order_no || '',
                warehouse: item.warehouse || 'Delivery',
                companyName: item.company_name || '',
                companyAddress: item.address || '',
                contactName: item.contact_name || '',
                countryCode: item.country_code || this.defaultCountryCode || '',
                phone: item.phone || '',
                altPhone: item.alt_phone || '',
                email: item.email || '',
                deliveryDate: item.dely_date ? item.dely_date.split('T')[0] : this.today,
                pickupDate: item.pickup_date ? item.pickup_date.split('T')[0] : this.today,
                deliveryTime: item.dely_time || '',
                pickupTime: item.pickup_time || '',
                serviceTime: item.service_time || 't15',
                driver_id: item.driver_id || null,
                signReq: item.signature_required || 'No',
                delPU: item.first_delivery !== null && item.first_delivery !== undefined ? item.first_delivery : 0,
                note: item.note || '',
                showCompanyDropdown: false,
                showAddressDropdown: false,
                filteredCompanies: [],
                filteredAddressOptions: [],
                highlightedCompanyIndex: -1,
                highlightedAddressIndex: -1,
                isAddressNew: item.is_address_new || false,
                isCompanyNew: item.is_company_new || false,
                matchingCompanies: item.matching_companies || [],
                matchingAddresses: item.matching_addresses || [],
                addressId: (!item.is_address_new && item.matching_addresses?.length === 1) ? item.matching_addresses[0].id : null,
                fromDispatchSheet: true,
                row_uuid: item.row_uuid || '',
                sheetContactName: item.contact_name || '',
                sheetPhone: item.phone || ''
              };
            });

            const mergedRows = [...draftRows, ...sheetRows];
            const totalNeeded = Math.max(10, mergedRows.length);
            while (mergedRows.length < totalNeeded) {
              mergedRows.push(this.createEmptyRow(mergedRows.length + 1));
            }
            this.rows = mergedRows;

            this.hasFromDispatchSheet = true;
            this.isDataModified = true;
            localStorage.setItem('isDataModified', 'true');
          } else {
            const draftRows = this.rows
              .filter(row => row.isBackendOrder || (row.id && !row.fromDispatchSheet && !row.row_uuid))
              .map((row, idx) => ({ ...row, rowNumber: idx + 1 }));
            while (draftRows.length < 10) {
              draftRows.push(this.createEmptyRow(draftRows.length + 1));
            }
            this.rows = draftRows;
            this.toast.show('warning', 'Uploaded sheet does not contain any orders');
          }
        } else {
          this.toast.show('error', response?.resultDescription || 'Failed to import sheet');
        }

        if (this.fileInput && this.fileInput.nativeElement) {
          this.fileInput.nativeElement.value = '';
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.show('error', 'An error occurred during file upload. Please try again.');
        if (this.fileInput && this.fileInput.nativeElement) {
          this.fileInput.nativeElement.value = '';
        }
        this.cdr.markForCheck();
      }
    });
  }


  /**
   * Navigate back
   */
  goBack(): void {
    // Use Angular Router to navigate back
    // this.router.navigate(['/orders']);
    window.history.back();
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByRowNumber(index: number, row: BulkOrderRow): number {
    return row.rowNumber;
  }

  calculateNextWorkingDate(currentDate: Date): Date {
    let nextDate = new Date(currentDate);

    // Increment date until a working day is found
    do {
      nextDate.setDate(nextDate.getDate() + 1); // Go to the next day
    } while (nextDate.getDay() === 0 || nextDate.getDay() === 6); // Skip weekends

    return nextDate;
  }
  dateFormat(d: any): string {
    if (typeof d === 'object') {
      const date = d;
      return (
        date.getFullYear() +
        '-' +
        this.padNumber(date.getMonth() + 1) +
        '-' +
        this.padNumber(date.getDate())
      );
    } else {
      return d;
    }
  }
  padNumber(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }
  loadDraftData(): void {
    this.orderService.getallDraftData().subscribe({
      next: (response: any) => {
        let backendRows: any[] = [];
        if (response && response.result && response.result.results && response.result.results.length > 0) {
          backendRows = response.result.results.map((item: any, index: number) => ({
            id: item.id || '',
            rowNumber: index + 1,
            orderNo: [
              item.order_no ? item.order_no : item.po,
              item.order_no ? item.dely_multiple_order_ids : item.pickup_multiple_order_ids
            ].filter(val => val)
              .map(val => typeof val === 'string' ? val.trim() : val)
              .join(', '),
            warehouse: item.order_type === 'delivery' ? 'Delivery' : 'Pickup',
            companyName: item.order_type === 'delivery' ? item.dely_company_name : item.pickup_company_name || '',
            companyAddress: item.customer_addr || '',
            contactName: item.customer_name || '',
            countryCode: item.country_code || '',
            phone: item.customer_phone || '',
            altPhone: item.customer_alt_phone || '',
            email: item.customer_email || '',
            deliveryDate: item.dely_date ? item.dely_date.split('T')[0] : '',
            pickupDate: item.pickup_date ? item.pickup_date.split('T')[0] : '',
            deliveryTime: this.getFirstPart(item?.dely_time) || '',
            pickupTime: this.getFirstPart(item.pickup_time) || '',
            serviceTime: item.order_type === 'delivery' ? item.dely_time_interval : item.pickup_time_interval || 't15',
            driver_id: item.current_driver?.id || null,
            signReq: item.order_type === 'delivery'
              ? (item.dely_signature_required ? 'Yes' : 'No')
              : (item.pickup_signature_required ? 'Yes' : 'No'),
            delPU: item.order_type === 'delivery'
              ? (item.is_dely_first_delivery ? item.is_dely_first_delivery : 0)
              : (item.is_pickup_first_delivery ? item.is_pickup_first_delivery : 0),
            note: item.note || '',
            isBackendOrder: true,
            showCompanyDropdown: false,
            filteredCompanies: [],
            highlightedCompanyIndex: -1,
            isAddressNew: item.is_address_new || false,
            isCompanyNew: item.is_company_new || false,
            matchingCompanies: item.matching_companies || [],
            matchingAddresses: item.matching_addresses || [],
            addressId: (!item.is_address_new && item.matching_addresses?.length === 1) ? item.matching_addresses[0].id : (item.address_id || item.customer_addr_id || item.address?.id || null),
            fromDispatchSheet: item.from_dispatch_sheet || false,
            row_uuid: item.row_uuid || '',
            sheetContactName: item.contact_name || item.customer_name || '',
            sheetPhone: item.phone || item.customer_phone || '',
            orders_logs: item.orders_logs || null
          }));
        }
        // 🔹 Ensure at least 10 rows in the table
        const totalRows = Math.max(10, backendRows.length);
        this.rows = [];
        for (let i = 0; i < totalRows; i++) {
          if (i < backendRows.length) {
            this.rows.push(backendRows[i]); // fill backend data
          } else {
            const savedCountryCode = localStorage.getItem('countryCode');
            this.rows.push({
              id: '',
              rowNumber: i + 1,
              orderNo: '',
              warehouse: '',
              companyName: '',
              companyAddress: '',
              contactName: '',
              countryCode: savedCountryCode || '',
              phone: '',
              altPhone: '',
              email: '',
              deliveryDate: this.today,
              pickupDate: this.today,
              deliveryTime: '',
              pickupTime: '',
              serviceTime: 't15',
              driver_id: null,
              signReq: 'No',
              delPU: 0,
              note: '',
              showCompanyDropdown: false,
              showAddressDropdown: false,
              filteredCompanies: [],
              filteredAddressOptions: [],
              highlightedCompanyIndex: -1,
              highlightedAddressIndex: -1,
              fromDispatchSheet: false,
            });
          }
        }
        // Force update reference for OnPush and dropdowns
        this.rows = [...this.rows];
        this.isDraftLoaded = true;
        this.checkLoadingComplete();
      },
      error: (error) => {
        console.error('Failed to fetch draft orders:', error);
        this.toast.show('error', 'Failed to load draft orders.');

        // 🔹 Error case → still show 10 empty rows
        this.rows = [];
        for (let i = 0; i < 10; i++) {
          const savedCountryCode = localStorage.getItem('countryCode');
          this.rows.push({
            id: '',
            rowNumber: i + 1,
            orderNo: '',
            warehouse: '',
            companyName: '',
            companyAddress: '',
            contactName: '',
            countryCode: savedCountryCode || '',
            phone: '',
            altPhone: '',
            email: '',
            deliveryDate: this.today,
            pickupDate: this.today,
            deliveryTime: '',
            pickupTime: '',
            serviceTime: 't15',
            driver_id: null,
            signReq: 'No',
            delPU: 0,
            note: '',
            showCompanyDropdown: false,
            showAddressDropdown: false,
            filteredCompanies: [],
            filteredAddressOptions: [],
            highlightedCompanyIndex: -1,
            highlightedAddressIndex: -1,
          });
        }
        this.isDraftLoaded = true;
        this.checkLoadingComplete();
      }

    });
  }

  openErrorModal(invalidRows: any[], add_star?: any, is_merge?: any) {
    const dialogRef = this.dialog.open(ErrormodalComponent, {
      data: {
        invalidRows: invalidRows,
        add_star: add_star,
        is_merge: is_merge
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result == 'Remain_same') {
          return
        }
        if (result.add_star == true) {
          this.updateDuplicateOrderNos(result.invalidRows);
        }
        if (result.invalidRows) {
          // y use case same company name addfress and date and diffrent order number
          if (result.invalidRows[0].errors.includes('pending_order_exist')) {
            result = result.invalidRows
            const mergeData = result.map((row: any) => {
              const originalRow = this.rows.find(r => r.rowNumber === row.column_data.rownumber);
              return {
                rownumber: row.column_data.rownumber,
                order_id: row.column_data.order_id || originalRow?.id || null,
                order_no: row.column_data.order_no,
                address: row.column_data.address,
                company: row.column_data.company_name,
                warehouse: row.column_data.warehouse,
                pickup_date: row.column_data.pickup_date,
                dely_date: row.column_data.dely_date
              };
            });
            const payload = { merge_data: mergeData };

            this.orderService.sendMergeData(payload).subscribe({
              next: (res) => {
                this.toast.show('success', res?.resultDescription);
                if (this.rows.some(row => row.fromDispatchSheet)) {
                  this.hasFromDispatchSheet = true;
                }
                const mergedRowNumbers = mergeData.map((item: { rownumber: any; }) => item.rownumber);

                let remainingRows = this.rows.filter(row => !mergedRowNumbers.includes(row.rowNumber));

                const rowsToAdd = this.rows.length - remainingRows.length;
                const removedRows = this.rows.filter(row => mergedRowNumbers.includes(row.rowNumber));
                removedRows.forEach(removedRow => {
                  const matchedRow = remainingRows.find(row =>
                    row.warehouse === removedRow.warehouse &&
                    row.companyName === removedRow.companyName &&
                    row.companyAddress === removedRow.companyAddress &&
                    (
                      row.warehouse === 'delivery'
                        ? row.deliveryDate === removedRow.deliveryDate
                        : row.pickupDate === removedRow.pickupDate
                    )
                  );

                  if (matchedRow) {
                    // Clean up and merge order numbers
                    const existingOrders = matchedRow.orderNo.split(',').map((o: string) => o.trim());
                    const newOrders = removedRow.orderNo.split(',').map((o: string) => o.trim());

                    // Avoid duplicate entries
                    const mergedOrders = Array.from(new Set([...existingOrders, ...newOrders]));

                    matchedRow.orderNo = mergedOrders.join(', ');
                  }
                });

                this.rows = remainingRows;
                const savedCountryCode = localStorage.getItem('countryCode');
                const blankRows = Array.from({ length: rowsToAdd }, () => ({
                  id: '',
                  rowNumber: 0,
                  orderNo: '',
                  warehouse: '',
                  companyName: '',
                  companyAddress: '',
                  contactName: '',
                  countryCode: savedCountryCode || '',
                  phone: '',
                  altPhone: '',
                  email: '',
                  deliveryDate: this.today,
                  pickupDate: this.today,
                  deliveryTime: '',
                  pickupTime: '',
                  serviceTime: 't15',
                  driver_id: '',
                  signReq: 'No',
                  delPU: 0,
                  note: '',
                  showCompanyDropdown: false,
                  showAddressDropdown: false,
                  filteredCompanies: [],
                  filteredAddressOptions: [],
                  highlightedCompanyIndex: -1,
                  highlightedAddressIndex: -1,
                }));

                this.rows = [...remainingRows, ...blankRows].map((row, index) => ({
                  ...row,
                  rownumber: index + 1
                }));
                this.cdr.markForCheck();
                this.saveDraftBtn(true);
              },
              error: () => {
                // console.error('❌ API Error:', err);
              }
            });
          }
        }
      }
      if (result == null) {
        console.log(invalidRows)
        const generatedKeys: string[] = [];

        invalidRows.forEach((row: any) => {
          const column = row.column_data;
          const address = column.address;
          const company = column.company_name;
          const warehouse = column.warehouse;

          const rawDate = warehouse === 'pickup' ? column.pickup_date : column.dely_date;
          const date = rawDate?.split('T')[0]; // ✅ Extract just the date part

          const key = `${warehouse}::${company}::${address}::${date}`.toLowerCase();
          generatedKeys.push(key);
        });
        localStorage.setItem('skip_draft_orders_saved', JSON.stringify(generatedKeys));
        this.saveDraftBtn();

      }

      if (result == 'nulles') {
        invalidRows.forEach((invalidRow: any) => {
          const hasPhoneError = invalidRow.errors?.includes('Phone number');
          const hasContactError = invalidRow.errors?.includes('Contact name');
          this.rows = this.rows.map(row => {
            const isSameRow = JSON.stringify(row.rowNumber) === JSON.stringify(invalidRow.column_data.rownumber); // match by content
            if (isSameRow && hasPhoneError) {
              return {
                ...row,
                phoneError: true,
              };
            }
            if (isSameRow && hasContactError) {
              return {
                ...row,
                contactNameError: true
              };
            }
            return row;
          });
        });

      }
    });
  }
  openAddOrderErrorModal(invalidRows: any[], add_star?: any, is_merge?: any) {
    const dialogRef = this.dialog.open(ErrormodalComponent, {
      data: {
        invalidRows: invalidRows,
        add_star: add_star,
        is_merge: is_merge
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result == 'Remain_same') {
          return
        }
        if (result.add_star == true) {
          this.addUpdateDuplicateOrderNos(result.invalidRows);
        }
        if (result.invalidRows) {
          // y use case same company name addfress and date and diffrent order number
          if (result.invalidRows[0].errors.includes('pending_order_exist')) {
            result = result.invalidRows
            const mergeData = result.map((row: any) => {
              const originalRow = this.rows.find(r => r.rowNumber === row.column_data.rownumber);
              return {
                rownumber: row.column_data.rownumber,
                order_id: row.column_data.order_id || originalRow?.id || null,
                order_no: row.column_data.order_no,
                address: row.column_data.address,
                company: row.column_data.company_name,
                warehouse: row.column_data.warehouse,
                pickup_date: row.column_data.pickup_date,
                dely_date: row.column_data.dely_date
              };
            });
            const payload = { merge_data: mergeData };

            this.orderService.sendMergeData(payload).subscribe({
              next: (res) => {
                this.toast.show('success', res?.resultDescription);
                if (this.rows.some(row => row.fromDispatchSheet)) {
                  this.hasFromDispatchSheet = true;
                }
                const mergedRowNumbers = mergeData.map((item: { rownumber: any; }) => item.rownumber);

                let remainingRows = this.rows.filter(row => !mergedRowNumbers.includes(row.rowNumber));

                const rowsToAdd = this.rows.length - remainingRows.length;
                const removedRows = this.rows.filter(row => mergedRowNumbers.includes(row.rowNumber));
                removedRows.forEach(removedRow => {
                  const matchedRow = remainingRows.find(row =>
                    row.warehouse === removedRow.warehouse &&
                    row.companyName === removedRow.companyName &&
                    row.companyAddress === removedRow.companyAddress &&
                    (
                      row.warehouse === 'delivery'
                        ? row.deliveryDate === removedRow.deliveryDate
                        : row.pickupDate === removedRow.pickupDate
                    )
                  );

                  if (matchedRow) {
                    // Clean up and merge order numbers
                    const existingOrders = matchedRow.orderNo.split(',').map((o: string) => o.trim());
                    const newOrders = removedRow.orderNo.split(',').map((o: string) => o.trim());

                    // Avoid duplicate entries
                    const mergedOrders = Array.from(new Set([...existingOrders, ...newOrders]));

                    matchedRow.orderNo = mergedOrders.join(', ');
                  }
                });

                this.rows = remainingRows;
                const savedCountryCode = localStorage.getItem('countryCode');
                const blankRows = Array.from({ length: rowsToAdd }, () => ({
                  id: '',
                  rowNumber: 0,
                  orderNo: '',
                  warehouse: '',
                  companyName: '',
                  companyAddress: '',
                  contactName: '',
                  countryCode: savedCountryCode || '',
                  phone: '',
                  altPhone: '',
                  email: '',
                  deliveryDate: this.today,
                  pickupDate: this.today,
                  deliveryTime: '',
                  pickupTime: '',
                  serviceTime: 't15',
                  driver_id: '',
                  signReq: 'No',
                  delPU: 0,
                  note: '',
                  showCompanyDropdown: false,
                  showAddressDropdown: false,
                  filteredCompanies: [],
                  filteredAddressOptions: [],
                  highlightedCompanyIndex: -1,
                  highlightedAddressIndex: -1,
                }));

                this.rows = [...remainingRows, ...blankRows].map((row, index) => ({
                  ...row,
                  rownumber: index + 1
                }));
                this.cdr.markForCheck();
                this.addOrder(true);
              },
              error: () => {
                // console.error('❌ API Error:', err);
              }
            });
          }
        }
      }
      if (result == null) {
        console.log(invalidRows)
        const generatedKeys: string[] = [];

        invalidRows.forEach((row: any) => {
          const column = row.column_data;
          const address = column.address;
          const company = column.company_name;
          const warehouse = column.warehouse;

          const rawDate = warehouse === 'pickup' ? column.pickup_date : column.dely_date;
          const date = rawDate?.split('T')[0]; // ✅ Extract just the date part

          const key = `${warehouse}::${company}::${address}::${date}`.toLowerCase();
          generatedKeys.push(key);
        });
        localStorage.setItem('skip_draft_orders_saved', JSON.stringify(generatedKeys));
        this.addOrder();

      }

      if (result == 'nulles') {
        invalidRows.forEach((invalidRow: any) => {
          const hasPhoneError = invalidRow.errors?.includes('Phone number');
          const hasContactError = invalidRow.errors?.includes('Contact name');
          this.rows = this.rows.map(row => {
            const isSameRow = JSON.stringify(row.rowNumber) === JSON.stringify(invalidRow.column_data.rownumber); // match by content
            if (isSameRow && hasPhoneError) {
              return {
                ...row,
                phoneError: true,
              };
            }
            if (isSameRow && hasContactError) {
              return {
                ...row,
                contactNameError: true
              };
            }
            return row;
          });
        });

      }
    });
  }
  updateDuplicateOrderNos(invalidRows: any[]) {
    const globalOrderNoMap: Map<string, { row: any; index: number; row_number: number }[]> = new Map();

    const rowOrderErrorCountMap = new Map<string, number>(); // row_number__orderNo => count

    // Step 1: Count how many times each orderNo appears in invalidRows per row_number
    invalidRows.forEach((invalidRow: any) => {
      const errorMessage = invalidRow.errors || '';
      const match = errorMessage.match(/Order No ([\w-]+)/i);
      const orderNo = match ? match[1] : null;
      const rowNumber = invalidRow.row_number;

      if (orderNo) {
        const key = `${rowNumber}__${orderNo}`;
        rowOrderErrorCountMap.set(key, (rowOrderErrorCountMap.get(key) || 0) + 1);
      }
    });

    // Step 2: Build global map of all orderNos with their position in data
    this.rows.forEach((row, i) => {
      if (row.orderNo) {
        const orderNos = row.orderNo.split(',').map((no: string) => no.trim());
        const rowNumber = row.rowNumber || i + 1;

        orderNos.forEach((no: string, index: number) => {
          const cleanNo = no.replace(/\*$/, '');
          const key = cleanNo;

          const entry = {
            row,
            index,
            row_number: row.rowNumber,
          };

          if (globalOrderNoMap.has(key)) {
            globalOrderNoMap.get(key)!.push(entry);
          } else {
            globalOrderNoMap.set(key, [entry]);
          }
        });
      }
    });

    // Step 3: Loop through each orderNo entry and apply * where needed
    globalOrderNoMap.forEach((entries, orderNo) => {
      const groupedByRow = new Map<number, { row: any; indexes: number[] }>();

      // Group all positions of same orderNo by row_number
      entries.forEach(({ row, index, row_number }) => {
        if (!groupedByRow.has(row_number)) {
          groupedByRow.set(row_number, { row, indexes: [] });
        }
        groupedByRow.get(row_number)!.indexes.push(index);
      });

      groupedByRow.forEach(({ row, indexes }, row_number) => {
        const key = `${row_number}__${orderNo}`;
        const starCount = rowOrderErrorCountMap.get(key) || 0;
        const cleanOrderNo = orderNo.replace(/\*$/, '');


        if (starCount >= 0) {
          const orderNos = row.orderNo.split(',').map((no: string) => no.trim());

          let applied = 0;
          for (let i of indexes) {
            const original = orderNos[i];
            const clean = original.replace(/\*$/, '');
            if (original.endsWith('*')) {
              // already has star, leave it untouched
              continue;
            }
            if (clean === orderNo && applied < starCount) {
              orderNos[i] = orderNo + '*';
              applied++;
            } else {
              orderNos[i] = orderNo;
            }
          }

          row.orderNo = orderNos.join(', ');
        }
      });
    });
    this.saveDraftBtn();
  }
  addUpdateDuplicateOrderNos(invalidRows: any[]) {
    const globalOrderNoMap: Map<string, { row: any; index: number; row_number: number }[]> = new Map();

    const rowOrderErrorCountMap = new Map<string, number>(); // row_number__orderNo => count

    // Step 1: Count how many times each orderNo appears in invalidRows per row_number
    invalidRows.forEach((invalidRow: any) => {
      const errorMessage = invalidRow.errors || '';
      const match = errorMessage.match(/Order No ([\w-]+)/i);
      const orderNo = match ? match[1] : null;
      const rowNumber = invalidRow.row_number;

      if (orderNo) {
        const key = `${rowNumber}__${orderNo}`;
        rowOrderErrorCountMap.set(key, (rowOrderErrorCountMap.get(key) || 0) + 1);
      }
    });

    // Step 2: Build global map of all orderNos with their position in data
    this.rows.forEach((row, i) => {
      if (row.orderNo) {
        const orderNos = row.orderNo.split(',').map((no: string) => no.trim());
        const rowNumber = row.rowNumber || i + 1;

        orderNos.forEach((no: string, index: number) => {
          const cleanNo = no.replace(/\*$/, '');
          const key = cleanNo;

          const entry = {
            row,
            index,
            row_number: row.rowNumber,
          };

          if (globalOrderNoMap.has(key)) {
            globalOrderNoMap.get(key)!.push(entry);
          } else {
            globalOrderNoMap.set(key, [entry]);
          }
        });
      }
    });

    // Step 3: Loop through each orderNo entry and apply * where needed
    globalOrderNoMap.forEach((entries, orderNo) => {
      const groupedByRow = new Map<number, { row: any; indexes: number[] }>();

      // Group all positions of same orderNo by row_number
      entries.forEach(({ row, index, row_number }) => {
        if (!groupedByRow.has(row_number)) {
          groupedByRow.set(row_number, { row, indexes: [] });
        }
        groupedByRow.get(row_number)!.indexes.push(index);
      });

      groupedByRow.forEach(({ row, indexes }, row_number) => {
        const key = `${row_number}__${orderNo}`;
        const starCount = rowOrderErrorCountMap.get(key) || 0;
        const cleanOrderNo = orderNo.replace(/\*$/, '');


        if (starCount >= 0) {
          const orderNos = row.orderNo.split(',').map((no: string) => no.trim());

          let applied = 0;
          for (let i of indexes) {
            const original = orderNos[i];
            const clean = original.replace(/\*$/, '');
            if (original.endsWith('*')) {
              // already has star, leave it untouched
              continue;
            }
            if (clean === orderNo && applied < starCount) {
              orderNos[i] = orderNo + '*';
              applied++;
            } else {
              orderNos[i] = orderNo;
            }
          }

          row.orderNo = orderNos.join(', ');
        }
      });
    });
    this.addOrder();
  }
  isAlphaNumeric(event: any) {
    const inputElement = event.target;
    const currentPos = inputElement.selectionStart;

    // Allow backspace and arrow keys for navigation
    if (event.key === 'Backspace' || ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return; // Allow default backspace behavior and navigation
    }

    // Prevent space or tab key input
    // if (event.key === ' ' || event.key === 'Tab') {
    //   event.preventDefault();
    //   return;
    // }

    // Disallow symbols except alphanumeric, comma (`,`), and asterisk (`*`)
    const disallowedSymbols = /[^a-zA-Z0-9*,]/;
    if (disallowedSymbols.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Handle comma (`,`): insert a space after it and move cursor forward
    if (event.key === ',') {
      // Add a space after the comma
      setTimeout(() => {
        inputElement.value = inputElement.value.slice(0, currentPos) + ', ' + inputElement.value.slice(currentPos);
        inputElement.setSelectionRange(currentPos + 2, currentPos + 2); // Move cursor after the comma and space
      }, 0);
      event.preventDefault();
      return;
    }

    // Handle asterisk (`*`): allow it as an input
    if (event.key === '*') {
      return; // Let the input be handled by Angular's ngModel
    }

    // Allow alphanumeric characters
    if (event.key.match(/^[a-zA-Z0-9]$/)) {
      return; // Let the input be handled by Angular's ngModel
    }
  }
  /**
  * Submit all orders
  */
  addOrder(isAfterMerge: boolean = false): void {

    const filledRows = this.rows.filter(row =>
      row.orderNo || row.companyName || row.companyAddress
    );

    if (filledRows.length === 0 && !isAfterMerge) {
      this.toast.show('error', 'No valid rows to add order');
      localStorage.setItem('isDataModified', 'false');
      this.isDataModified = false;
      return;
    }

    if (filledRows.length > 0 && !this.validateFilledRows(filledRows)) {
      return;
    }

    this.isAddingOrder = true;

    // 🔹 Backup original values (like addDraft)
    const savedPickupTimes = filledRows.map(row => row.pickupTime);
    const savedDelyTimes = filledRows.map(row => row.deliveryTime);
    const savedPickupDates = filledRows.map(row => row.pickupDate);
    const savedDelyDates = filledRows.map(row => row.deliveryDate);

    // 🔹 SAME DATE formatting logic
    filledRows.forEach(row => {
      const c = row.pickupDate
        ? this.dateMomentObjFormat(row.pickupDate) + ' ' + this.getTime(true)
        : null;
      row.pickupDate = c ? this.getFormattedDate(c) : 'null';

      const d = row.deliveryDate
        ? this.dateMomentObjFormat(row.deliveryDate) + ' ' + this.getTime(true)
        : null;
      row.deliveryDate = d ? this.getFormattedDate(d) : 'null';
    });

    // 🔹 SAME TIME formatting logic
    filledRows.forEach(row => {
      const timeFormatRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i;

      row.pickupTime = row.pickupTime
        ? (timeFormatRegex.test(row.pickupTime)
          ? this.timeConverter.transform(row.pickupTime, '12-24')
          : row.pickupTime)
        : null;

      row.deliveryTime = row.deliveryTime
        ? (timeFormatRegex.test(row.deliveryTime)
          ? this.timeConverter.transform(row.deliveryTime, '12-24')
          : row.deliveryTime)
        : null;
    });
    const skipKeysRaw = localStorage.getItem('skip_draft_orders_saved');
    const skip_draft_orders_saved: string[] = skipKeysRaw ? JSON.parse(skipKeysRaw) : [];

    // 🔹 Payload build (unchanged mapping)
    const draftPayload = {
      orders: filledRows.map(row => ({
        is_draft: 'false',
        order_id: row.id || null,
        rownumber: row.rowNumber,

        order_no: row.orderNo || null,
        warehouse: row.warehouse || null,
        company_name: row.companyName || null,
        address: row.companyAddress || null,
        contact_name: row.contactName || null,

        country_code: row.countryCode || null,
        phone: row.phone || null,
        alt_phone: row.altPhone === 'null' ? null : row.altPhone,
        email: row.email || null,

        dely_date: row.deliveryDate,
        pickup_date: row.pickupDate,
        dely_time: row.deliveryTime,
        pickup_time: row.pickupTime,
        service_time: row.serviceTime || null,

        driver_id: row.driver_id || null,
        signature_required: row.signReq || 'No',
        first_delivery: (row.delPU !== null && row.delPU !== undefined) ? row.delPU : 'No',
        note: row.note || null,
        ...(row.orders_logs ? { orders_logs: row.orders_logs } : {}),
      })),
      skip_draft_orders_saved: skip_draft_orders_saved,
      from_dispatch_sheet: filledRows.some(row => row.fromDispatchSheet) || this.rows.some(row => row.fromDispatchSheet) || this.hasFromDispatchSheet
    };

    // // 🔥 Print EXACT API payload
    // console.log('🚀 Draft API Payload:', JSON.stringify(draftPayload, null, 2));

    // alert(`Draft saved with ${filledRows.length} orders!`);

    // 🔁 Restore original values (important, like addDraft)
    filledRows.forEach((row, index) => {
      row.pickupTime = savedPickupTimes[index];
      row.deliveryTime = savedDelyTimes[index];
      row.pickupDate = savedPickupDates[index];
      row.deliveryDate = savedDelyDates[index];
    });
    this.orderService.addMultipledraftOrders(draftPayload).subscribe({
      next: (response: any) => {
        this.isAddingOrder = false;
        if (response.resultCode === '1') {
          this.toast.show('success', response.resultDescription);
          this.loadDraftData();
          localStorage.setItem('isDataModified', 'false');
          this.isDataModified = false;
          this.cdr.markForCheck();
          localStorage.removeItem('skip_draft_orders_saved');
          localStorage.removeItem('ordersCache');
          localStorage.removeItem('arkiveOrdersCache');
          localStorage.removeItem('nextPageUrl');
          localStorage.removeItem('arkiveNextPageUrl');
          localStorage.setItem('isSearchApplied', 'true');
          localStorage.setItem('pageIndex', '0');
          localStorage.setItem('fromOrderDetail', 'false');
          this.router.navigate(['/admin/dashboard']);
          // setTimeout(() => {
          //   location.reload();
          // }, 500);
        }
        if (response.resultCode === '0') {
          if (response.invalidRows && response.invalidRows.length > 0) {
            this.openAddOrderErrorModal(response.invalidRows, response.add_star, response.is_merge);
          } else {
            if (response.errorMessage) {
              this.toast.show('error', response.errorMessage);
            }
          }
          filledRows.forEach((order, index) => {
            order.pickupTime = savedPickupTimes[index];
            order.deliveryTime = savedDelyTimes[index];
            order.deliveryDate = savedPickupDates[index];
            order.pickupDate = savedDelyDates[index];
          });
        }
        if (response.resultCode === '4') {
          this.authService.doLogout();
          this.toast.show('success', 'Logout!');
        }
      },
      error: (error) => {
        console.error('Add Order API Error:', error);
        this.toast.show('error', 'Failed to add orders. Please try again.');
        this.isAddingOrder = false;
        this.cdr.markForCheck();
      },
    });


    // 🔹 Actual API call (when ready)
    // this.orderService.addMultipledraftOrders(draftPayload).subscribe(...)
  }
  openDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string,
    row: any
  ): void {
    const dialogRef = this.dialog.open(AddAddressListingComponent, {
      width: '800px',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        isCloseBtn: true,
        addressText: row.companyAddress
      },
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        if (res && res.action === 'saveDraft') {
          this.addressData = [];
          this.addressListss(1, row.rowNumber);
        }
      }
    });
  }
  addressData: any;
  //  addressListss(page: any, rowNum?: any) {
  //   this.addressService.addressSearch(page);
  //   this.address$ = this.addressService.getAddress();

  //   this.address$
  //     .pipe(take(1))   // 👈 IMPORTANT FIX
  //     .subscribe((data: any) => {
  //       this.addressData = data?.result?.results;

  //       setTimeout(() => {
  //         const matchedRow = this.rows.find(row => row.rowNumber === rowNum);

  //         if (matchedRow) {
  //           matchedRow.companyAddress = this.addressData[0].address;
  //         }

  //       }, 1000);
  //     });
  // }
  addressListss(page: any, rowNum?: any) {

    this.addressService.getAddressListing(page).subscribe((data: any) => {

      this.addressData = data?.result?.results;

      if (this.addressData && this.addressData.length) {
        this.addressData.forEach((newItem: any) => {
          if (newItem.address && !this.initialAddresses.some(a => a.id === newItem.id || a.address === newItem.address)) {
            this.initialAddresses.push(newItem);
          }
        });
      }

      this.rows = this.rows.map(row => {
        if (row.rowNumber === rowNum && this.addressData?.length) {
          return { ...row, companyAddress: this.addressData[0].address, addressId: this.addressData[0].id, isAddressNew: false, matchingAddresses: [], showAddButton: false };
        }
        return row;
      });
      this.cdr.markForCheck();
    }, error => {
      this.authService.dataError(error);
    });

  }

  hasImportantData(): boolean {
    return this.rows.some(row =>
      (row.orderNo && row.orderNo.trim() !== '') ||
      (row.companyAddress && row.companyAddress.trim() !== '') ||
      (row.companyName && row.companyName.trim() !== '')
      || (row.warehouse && row.warehouse.trim() !== '')
    );
  }
  saveTimeout: any; // Timer reference
  AUTO_SAVE_DELAY = 5 * 60 * 1000; // 15 minutes
  clearAutoSaveTimer() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }
  resetAutoSaveTimer() {
    this.clearAutoSaveTimer(); // Clear previous timer

    // Start a new timer
    this.saveTimeout = setTimeout(() => {
      this.saveDraft();
    }, this.AUTO_SAVE_DELAY);
  }
  ngOnDestroy() {
    this.clearAutoSaveTimer();
    if (this.countryCodeSubscription) {
      this.countryCodeSubscription.unsubscribe();
    }
  }
  getFirstPart(time: string | null): string | undefined {
    if (!time || time.trim() === "0") {
      return undefined;
    }

    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      const [hours, minutes] = time.split(":").map(Number);
      let period = "AM";
      let displayHour = hours % 12 || 12;
      if (hours >= 12) {
        period = "PM";
      }
      return `${displayHour}:${minutes.toString().padStart(2, "0")} ${period}`;
    }

    const parts = time.split(" ");
    const value = parseFloat(parts[0]);
    const unit = parts[1]?.toLowerCase();

    if (!unit) {
      return undefined;
    }

    let totalMinutes: number;

    if (unit === "hours" || unit === "hour" || unit === "hrs" || unit === "hr") {
      totalMinutes = Math.floor(value * 60);
    } else if (unit === "days" || unit === "day") {
      totalMinutes = Math.floor(value * 24 * 60);
    } else if (unit === "minutes" || unit === "minute" || unit === "mins" || unit === "min") {
      totalMinutes = Math.floor(value);
    } else {
      return undefined;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    let period = "AM";
    let displayHour = hours % 12 || 12;
    if (hours >= 12) {
      period = "PM";
    }
    return `${displayHour}:${minutes.toString().padStart(2, "0")} ${period}`;
  }

  isGenericContactName(name?: string): boolean {
    if (!name) return true;
    const cleanName = name.trim().toLowerCase();
    const genericNames = ['receiving dept', 'shipping dept', 'receiving', 'shipping', 'customer', 'dept', 'department'];
    return genericNames.includes(cleanName);
  }

  isGenericPhoneNumber(phone?: string): boolean {
    if (!phone) return true;
    const cleanPhone = phone.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 7) return true;
    const dummyPhones = ['9999999999', '8888888888', '999999999', '0000000000', '1234567890'];
    if (dummyPhones.includes(cleanPhone)) return true;
    if (/^(\d)\1+$/.test(cleanPhone)) return true;
    return false;
  }

  resolveContactNames(sheetName?: string, apiName?: string): string {
    const splitNames = (val?: string): string[] => {
      if (!val) return [];
      return val
        .split(/[/|,&]/)
        .map(n => n.trim())
        .filter(n => n.length > 0 && !this.isGenericContactName(n));
    };

    const sheetList = splitNames(sheetName);
    const apiList = splitNames(apiName);

    const result: string[] = [];
    const seen = new Set<string>();

    const addName = (name: string) => {
      const lower = name.toLowerCase();
      if (!seen.has(lower) && result.length < 2) {
        seen.add(lower);
        result.push(name);
      }
    };

    sheetList.forEach(addName);
    apiList.forEach(addName);

    if (result.length === 0) {
      return (sheetName || apiName || '').trim();
    }

    return result.join(' / ');
  }

  addrssId: any;
  onCompanySelection(companyName: string, companyAddress: string, rowNumber: number) {
    if (companyName && companyAddress) {
      const row = this.rows.find(n => n.rowNumber === rowNumber);
      if (!row || !row.addressId) return;

      console.log(`Auto-populating row ${rowNumber} for Company: ${companyName}, Address ID: ${row.addressId}`);
      this.orderService.getOrderDatas(companyName, row.addressId).subscribe(
        (res: any) => {
          if (res && res.result && res.result !== 'Record not found') {
            const apiContactName = this.sanitizeValue(
              res.result.order_type === 'delivery' ? res.result.dely_contact_name : res.result.pickup_contact_name
            );
            const apiCountryCode = this.sanitizeValue(res.result.country_code);
            const apiEmail = this.sanitizeValue(
              res.result.order_type === 'delivery' ? res.result.dely_email : res.result.pickup_email
            );
            const apiPhone = this.sanitizeValue(
              res.result.order_type === 'delivery' ? res.result.dely_phone : res.result.pickup_phone
            );
            const apiAltPhone = this.sanitizeValue(
              res.result.order_type === 'delivery' ? res.result.dely_alt_phone : res.result.pickup_alt_phone
            );

            if (row.row_uuid) {
              const sheetName = row.sheetContactName?.trim();
              const sheetPhone = row.sheetPhone?.trim();

              const isPhoneGeneric = this.isGenericPhoneNumber(sheetPhone);
              const cleanSheetPhone = sheetPhone ? sheetPhone.replace(/\D/g, '') : '';
              const cleanApiPhone = apiPhone ? apiPhone.replace(/\D/g, '') : '';
              const isSamePhone = cleanSheetPhone && cleanApiPhone && cleanSheetPhone === cleanApiPhone;

              // 1. Contact Name resolution
              if (isSamePhone) {
                row.contactName = sheetName || apiContactName || '';
              } else {
                row.contactName = this.resolveContactNames(sheetName, apiContactName);
              }

              // 2. Phone resolution
              if (isPhoneGeneric) {
                row.phone = apiPhone || sheetPhone || '';
                row.altPhone = apiAltPhone;
              } else {
                row.phone = sheetPhone || apiPhone;

                if (isSamePhone) {
                  row.altPhone = apiAltPhone || '';
                } else {
                  row.altPhone = apiPhone || apiAltPhone;
                }
              }

              row.countryCode = apiCountryCode || row.countryCode;
              row.email = apiEmail || row.email;
            } else {
              row.contactName = this.resolveContactNames('', apiContactName);
              row.countryCode = apiCountryCode;
              row.email = apiEmail;
              row.phone = apiPhone;
              row.altPhone = apiAltPhone;
            }
          } else {
            // "Record not found" or no matching result case
            if (row.row_uuid) {
              row.contactName = row.sheetContactName || '';
              row.phone = row.sheetPhone || '';
              row.altPhone = '';
              row.email = '';
            } else {
              row.contactName = '';
              row.email = '';
              row.phone = '';
              row.altPhone = '';
            }
          }

          // Create a new reference for the row and update the rows array to trigger OnPush change detection
          const rowIndex = this.rows.findIndex(n => n.rowNumber === rowNumber);
          if (rowIndex !== -1) {
            this.rows[rowIndex] = { ...row };
            this.rows = [...this.rows];
            this.cdr.markForCheck();
          }
        },
        (error) => {
          console.error('Error fetching company details:', error);
          if (row.row_uuid) {
            row.contactName = row.sheetContactName || '';
            row.phone = row.sheetPhone || '';
            row.altPhone = '';
          }
        }
      );
    }
  }
  sanitizeValue(value: any): string {
    if (value === null || value === undefined || String(value).toLowerCase() === 'null') {
      return '';
    }
    return value;
  }

  getActiveDrivers(): void {
    this.orderService.getActiveDrivers().subscribe({
      next: (res) => {
        if (res && res.result) {
          this.driverOptions = [{ id: null, first_name: 'None', last_name: '' }, ...res.result.results];
          this.isDriversLoaded = true;
          this.checkLoadingComplete();
        } else {
          this.driverOptions = [{ id: null, first_name: 'None', last_name: '' }];
          this.isDriversLoaded = true;
          this.checkLoadingComplete();
        }
      },
      error: (err) => {
        console.error('Error while fetching drivers', err);
        this.isDriversLoaded = true; // Still mark as loaded to avoid infinite shimmer
        this.checkLoadingComplete();
      }
    });
  }

  private checkLoadingComplete(): void {
    if (this.isDriversLoaded && this.isDraftLoaded) {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }


  onRowFocus(rowNumber: any) {
    this.isDataModified = true;
    this.cdr.markForCheck();
    this.resetAutoSaveTimer();
  }

  scrollTable(direction: 'left' | 'right'): void {
    const container = document.getElementById('tableScrollContainer');
    if (container) {
      const scrollAmount = 400;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }

  scrollToExtreme(direction: 'left' | 'right'): void {
    const container = document.getElementById('tableScrollContainer');
    if (container) {
      container.scrollTo({
        left: direction === 'left' ? 0 : container.scrollWidth,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Validates that all mandatory fields are filled for rows that have some data
   */
  validateFilledRows(rows: BulkOrderRow[]): boolean {
    for (const row of rows) {
      const missingFields = [];
      if (!row.orderNo?.trim()) missingFields.push('Order Number');
      if (!row.warehouse?.trim()) missingFields.push('Warehouse');
      if (!row.companyName?.trim()) missingFields.push('Company Name');
      if (!row.companyAddress?.trim()) missingFields.push('Company Address');
      if (!row.contactName?.trim()) missingFields.push('Contact Name');
      if (!row.countryCode?.trim()) missingFields.push('Country Code');
      if (!row.phone?.trim()) {
        missingFields.push('Phone Number');
      } else if (row.phone.trim().length !== 10) {
        missingFields.push('Valid 10-digit Phone Number');
      }
      if (!row.deliveryDate) missingFields.push('Delivery Date');
      if (!row.pickupDate) missingFields.push('Pickup Date');
      if (!row.signReq) missingFields.push('Signature Required');

      if (missingFields.length > 0) {
        this.toast.show('error', `Row ${row.rowNumber} is missing: ${missingFields.join(', ')}`);
        return false;
      }
    }
    return true;
  }


  onCompanyAdded(companyName: string): void {
    if (!companyName) return;
    const targetCompany = companyName.trim().toLowerCase();

    // Cache the new company in initialCompanies to prevent subsequent exactMatch checks from flagging it as new
    const exists = this.initialCompanies.some(c => c.company?.toLowerCase() === targetCompany);
    if (!exists) {
      this.initialCompanies.push({ company: companyName.trim() });
    }

    this.rows.forEach(row => {
      if (row.companyName && row.companyName.trim().toLowerCase() === targetCompany) {
        row.isCompanyNew = false;
        row.matchingCompanies = [];
      }
    });
    this.cdr.markForCheck();
  }
}
